import {useEffect, useState} from 'react'
import _ from 'lodash'
import toast from 'react-hot-toast'
// Components
import FaIconLibrary from '../../icons/fa-icon-library'
// Utils
import {logDataByLevel} from '../../../utils/logging-helpers'
// Types
import type {SyntheticEvent} from 'react'
import type {BallotDisplayType} from '../../../types/ballot'
import type {GalleryEntry, PreviousVote} from '../../../types/photo-gallery'
// Styles
import styles from './photo-scroll-entry.module.scss'

interface PhotoScrollEntryProps {
  ballot?: any
  question?: any
  entry: GalleryEntry
  rankDictionary: Record<number, string> | null
  previousVote?: PreviousVote | null
  ballotDisplayType: BallotDisplayType | null | undefined
  currentWindowWidth?: number
  scrollHeight: number
  isSelected: boolean
  onSelectedAnswerChange: ((syntheticEvent: SyntheticEvent) => void) | null
  onOpenEntryImagesModal: (entry: GalleryEntry) => void
}

const PhotoScrollEntry = ({
  entry = {} as GalleryEntry,
  rankDictionary = null,
  ballotDisplayType = null,
  scrollHeight = 500,
  isSelected = false,
  onSelectedAnswerChange = null,
  onOpenEntryImagesModal,
}: PhotoScrollEntryProps) => {
  const [scrollEntry, setScrollEntry] = useState<GalleryEntry | null>(null)

  const [scrollEntryImageHeight, setScrollEntryImageHeight] = useState(0)
  const [scrollEntryImageWidth, setScrollEntryImageWidth] = useState(0)
  useEffect(() => {
    const newScrollEntryImageHeight = scrollHeight - 36
    setScrollEntryImageHeight(newScrollEntryImageHeight)
  }, [scrollHeight])

  useEffect(() => {
    let mounted = true
    if (mounted) {
      if (_.isEmpty(entry)) return
      let newScrollEntry = _.cloneDeep(entry)

      const scrollHeightOffset = 36 // Honestly, not sure what this number is
      const scrollCardFooterHeight = 42
      const optimizedCoverImage = `${entry.cover_image}-/resize/x${scrollHeight - scrollHeightOffset - scrollCardFooterHeight}/-/quality/smart/-/progressive/yes/`
      newScrollEntry['cover_image'] = optimizedCoverImage

      const rankDict = rankDictionary || {}
      const isRest = _.isEmpty(rankDict[entry.rank ?? 0]) || ballotDisplayType === 'photo-voting-finalist-announcement'
      const theRestRank = _.last(_.values(rankDict))
      const rankIndex = Math.min(entry.rank ?? 0, _.values(rankDict)?.length)

      const newRankDisplayText = isRest ? theRestRank : rankDict[rankIndex] || theRestRank
      newScrollEntry['rank_display_text'] = newRankDisplayText

      let newTopLevelStyles = `${styles['photo-scroll-entry']}`
      if (isSelected) {
        newTopLevelStyles += ` ${styles['--is-selected']}`
      }
      if (rankIndex < 3) {
        newTopLevelStyles += ` ${styles['--' + rankIndex.toString()]}`
      }
      newScrollEntry['top_level_classes'] = newTopLevelStyles
      setScrollEntry(newScrollEntry)

      const promise = new Promise<number>((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          const width = (img.width * (scrollHeight - scrollHeightOffset)) / img.height
          resolve(width)
        }

        img.onerror = () => {
          reject(new Error(`Failed to load image from URL: ${newScrollEntry.cover_image}`))
        }
        img.src = newScrollEntry.cover_image || ''
      })
      promise.then(width => {
        setScrollEntryImageWidth(width)
      }).catch(error => {
        logDataByLevel({
          level: 'error',
          message: 'Failed to load image from URL',
          data: error,
        })
      })
    }
    return () => {
      mounted = false
    }
  }, [entry])

  const handleShare = (entryId: string) => {
    const shareUrl = `${_.split(window.location.href, '?', 1)}?entry_id=${entryId}`
    navigator.clipboard.writeText(shareUrl)
    toast.success('Copied shared url to clipboard')
  }

  return scrollEntry
    ? <>
      {_.includes(['photo-voting-vote', 'photo-submissions-submit'], ballotDisplayType) && <>
        <div
          key={scrollEntry.key}
          className={`${styles['photo-scroll-entry']} ${isSelected ? styles['--is-selected'] : ''}`}
        >
          <div className={styles['__image-container']}>
            {scrollEntry.type === 'option' && <>
              <img
                role="button"
                aria-label={scrollEntry.photo_name || ''}
                src={scrollEntry.cover_image || ''}
                onClick={() => onOpenEntryImagesModal(scrollEntry)}
                className={styles['__image']}
                style={{height: `${scrollEntryImageHeight - 42}px`}}
              />
              <button
                type='button'
                onClick={() => onOpenEntryImagesModal(scrollEntry)}
                className={`button is-dark ${styles['__button-on-image']}`}
              >
                {(scrollEntry.photo_images_array?.length ?? 0) > 1 ? 'View All Photos' : 'View Full Image'}
              </button>
            </>}
            {ballotDisplayType === 'photo-submissions-submit' && scrollEntry.type === 'submission' && <>
              <div className={styles['__overlay']}>
                <div className={styles['__overlay-text']}>Once approved, your submission will appear here</div>
              </div>
              <img
                aria-label={scrollEntry.photo_name || ''}
                src={scrollEntry.cover_image || ''}
                className={styles['__image']}
                style={{height: `${scrollEntryImageHeight - 42}px`}}
              />
            </>}
          </div>
          <div className={styles['__footer']}>
            {scrollEntry.type === 'option' && <>
              <div className={styles['__title-bar']}>
                <div
                  style={{fontSize: `${Math.min(parseFloat((scrollHeight * 0.0075).toFixed(2)), 1.5)}em`, maxWidth: `${scrollEntryImageWidth}px`}}
                  className={styles['__title']}
                >
                  {`${scrollEntry.photo_name}`}
                </div>
              </div>
              <div className='buttons is-right'>
                <button
                  type='button'
                  onClick={() => handleShare(scrollEntry._id)}
                  className={`button ${styles['__button-share']}`}
                >
                  <FaIconLibrary icon='share-nodes' text='Share' showTextAfter={true} />
                </button>
                {isSelected &&
                  <div className={styles['__selected-vote-icon']}>
                    <FaIconLibrary icon='check-to-slot' size='2x' />
                  </div>
                }
                {!isSelected &&
                  <button
                    type='button'
                    onClick={syntheticEvent => onSelectedAnswerChange?.(syntheticEvent)}
                    className={`button is-dark ${styles['__button-vote']}`}
                  >
                    Cast vote
                  </button>
                }
              </div>
            </>}
            {ballotDisplayType === 'photo-submissions-submit' && scrollEntry.type === 'submission' && <>
              <div className={styles['__title-bar']}>
                <div
                  style={{fontSize: `${Math.min(parseFloat((scrollHeight * 0.0075).toFixed(2)), 1.5)}em`}}
                  className={styles['__title']}
                >
                </div>
              </div>
              <div className='buttons is-centered'>
                <button className='button is-dark is-large'>Continue ▼</button>
              </div>
            </>}
          </div>
        </div>
      </>}
      {_.includes(['photo-voting-results', 'photo-voting-finalist-announcement'], ballotDisplayType) && <>
        {scrollEntry.type === 'option' && <>
          <div
            key={scrollEntry.key}
            className={scrollEntry.top_level_classes}
          >
            <div className={styles['__image-container']}>
              <img
                src={scrollEntry.cover_image || ''}
                alt={scrollEntry.photo_name || ''}
                className={styles['__image']}
                style={{height: `${scrollEntryImageHeight - 42}px`}}
              />
              <button
                type='button'
                onClick={() => onOpenEntryImagesModal(scrollEntry)}
                className={`button is-dark ${styles['__button-on-image']}`}
              >
                {(scrollEntry.photo_images_array?.length ?? 0) > 1 ? 'View All Photos' : 'View Full Image'}
              </button>
            </div>
            <div className={styles['__footer']}>
              {scrollEntry.type === 'option' && <>
                <div className={styles['__title-bar']}>
                  <div
                    style={{fontSize: `${Math.min(parseFloat((scrollHeight * 0.0075).toFixed(2)), 1.5)}em`}}
                    className={styles['__title']}
                  >
                    {`${scrollEntry.photo_name}`}
                  </div>
                  <div className={styles['__rank-display']}>
                    {scrollEntry.rank_display_text}
                  </div>
                </div>
              </>}
            </div>
          </div>
        </>}
      </>}
    </>
    : null
}

export default PhotoScrollEntry
